"use client";

import { FilterName } from "@faire/deprecated-retailer-visitor-shared/components/Filters/FilterName";
import { MaybeDraggableStickyRowForLoggedOutPage } from "@faire/deprecated-retailer-visitor-shared/components/Filters/MaybeDraggableStickyRowForLoggedOutPage";
import { MobileToggleFiltersPill } from "@faire/deprecated-retailer-visitor-shared/components/Filters/MobileToggleFiltersPill";
import {
  Header,
  Sort,
} from "@faire/deprecated-retailer-visitor-shared/components/Filters/Sort";
import { ToggleFilterRow } from "@faire/deprecated-retailer-visitor-shared/components/Filters/ToggleFilterRow";
import { ToggleSidebarFiltersPill } from "@faire/deprecated-retailer-visitor-shared/components/Filters/ToggleSidebarFiltersPill";
import { useFilterScrollToProducts } from "@faire/deprecated-retailer-visitor-shared/components/Filters/useFilterScrollToProducts";
import { useFilterStore } from "@faire/deprecated-retailer-visitor-shared/components/Filters/useFilterStore";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { Core } from "@faire/design-tokens";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";

interface IProps {
  name: FilterName;
  className?: string;
  isLoading?: boolean;
  // Wrapper component to wrap the children without breaking the sticky behavior
  Wrapper?: React.FC<React.PropsWithChildren>;
  // Whether to show the filter collapse pill on tablet and below during SSR
  shouldNotShowFilterCollapsePillOnTabletAndBelow?: boolean;
  shouldShowSort?: boolean;
  shouldShowCarouselArrows?: boolean;
  shouldEnableScrollToProducts?: boolean;
}

export const FilterInfoSticky: React.FC<IProps> = ({
  name,
  className,
  isLoading,
  Wrapper = React.Fragment,
  shouldNotShowFilterCollapsePillOnTabletAndBelow = false,
  shouldShowSort,
  shouldEnableScrollToProducts = false,
  shouldShowCarouselArrows = false,
}) => {
  useTraceComponentRender("FilterInfoSticky");

  const isSSR = useIsSSR();
  const {
    data: { shouldShowFilterCollapsePill, filterSections },
  } = useFilterStore([
    "activeFilterCount",
    "shouldShowFilterCollapsePill",
    "filterSections",
  ]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useFilterScrollToProducts(
    scrollRef,
    !isLoading,
    shouldEnableScrollToProducts
  );

  const isLoggedInRetailer = useIsLoggedInRetailer();
  const [isDragging, setIsDragging] = useState(false);

  if ((isLoading === false && filterSections.length === 0) || isSSR) {
    return null;
  }

  return (
    <>
      {shouldEnableScrollToProducts ? <div ref={scrollRef} /> : null}
      <MaybeDraggableStickyRowForLoggedOutPage
        className={className}
        isLoading={isLoading}
        setIsDragging={setIsDragging}
        overflowVisible={shouldShowSort}
      >
        <Wrapper>
          {shouldShowFilterCollapsePill ? (
            <MaybeToggleSidebarFiltersPill
              shouldNotShowOnTabletAndBelow={
                shouldNotShowFilterCollapsePillOnTabletAndBelow
              }
            />
          ) : null}
          <MobileToggleFiltersPill name={name} />
          <StyledToggleFilterRow
            name={name}
            $useUpdatedSpacing={isLoggedInRetailer}
            $isDragging={isDragging}
            $addPaddingRight={shouldShowSort}
            shouldShowCarouselArrows={shouldShowCarouselArrows}
          />
        </Wrapper>
        {shouldShowSort ? (
          <ResponsiveContentV2
            desktopAndAbove={<StyledSort name={name} roundCorners />}
          />
        ) : null}
      </MaybeDraggableStickyRowForLoggedOutPage>
    </>
  );
};

const StyledSort = styled(Sort)`
  margin-left: auto;
  padding-top: ${getSpacing("1x")};
  padding-bottom: ${getSpacing("1x")};
  max-height: 56px;
  ${Header} {
    &:hover {
      border-color: ${Core.border.default};
    }
  }
`;

const StyledToggleFilterRow = styled(ToggleFilterRow)<{
  $useUpdatedSpacing: boolean;
  $isDragging: boolean;
  $addPaddingRight?: boolean;
}>`
  ${({ $isDragging }) =>
    $isDragging
      ? css`
          user-select: none;
          pointer-events: none;
        `
      : null}
  padding: ${getSpacing("1x", 0)};
  ${({ $addPaddingRight }) =>
    $addPaddingRight
      ? css`
          padding-right: ${getSpacing("1x")};
        `
      : null}
  &:empty {
    display: none;
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${({ $useUpdatedSpacing }) =>
      $useUpdatedSpacing ? getSpacing("1x", 0) : undefined};
  }
`;

const MaybeToggleSidebarFiltersPill = ({
  shouldNotShowOnTabletAndBelow,
}: {
  shouldNotShowOnTabletAndBelow: boolean;
}) => {
  return shouldNotShowOnTabletAndBelow ? (
    <ResponsiveContentV2
      noJS={true}
      desktopAndAbove={<ToggleSidebarFiltersPill />}
    />
  ) : (
    <ToggleSidebarFiltersPill />
  );
};
